import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ShieldCheck, Loader2, KeyRound, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ensurePushSubscribed, unsubscribePush, pushStatus } from "../lib/push";

const TwoFactor = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null); // {secret, qr_png_base64}
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  const [remaining, setRemaining] = useState(null);
  const [push, setPush] = useState({ supported: false });

  const loadStatus = async () => {
    const { data } = await api.get("/auth/me");
    setStatus(!!data.totp_enabled);
    if (data.totp_enabled) {
      const bc = await api.get("/auth/2fa/backup-codes");
      setRemaining(bc.data.remaining);
    }
    setPush(await pushStatus());
  };
  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetup(data);
    } finally { setBusy(false); }
  };

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code });
      setBackupCodes(data.backup_codes || []);
      toast.success("2FA ativado — anote os códigos de backup");
      setSetup(null); setCode("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    if (!window.confirm("Desativar 2FA? Você deixará sua conta menos segura.")) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code });
      toast.success("2FA desativado");
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Código inválido");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5" /> Segurança da conta
      </div>
      <h1 className="font-display font-bold text-3xl text-[#0B2B62] tracking-tight mt-1">Autenticação de dois fatores (2FA)</h1>
      <p className="text-slate-600 mt-2 text-sm">Adicione uma camada extra de proteção usando um app autenticador (Google Authenticator, Authy, 1Password, etc.).</p>

      {status === null ? (
        <div className="mt-8 text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando...</div>
      ) : backupCodes.length > 0 ? (
        <section className="mt-6 bg-white border-2 border-green-300 rounded-xl p-6" data-testid="backup-codes">
          <div className="flex items-center gap-2 text-green-700 font-semibold">
            <ShieldCheck className="w-5 h-5" /> Guarde estes 10 códigos de backup
          </div>
          <p className="text-sm text-slate-600 mt-2">Use um destes códigos se perder acesso ao app autenticador. Cada código funciona apenas <b>uma vez</b>. Anote e guarde em local seguro — você não verá esta tela novamente.</p>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
            {backupCodes.map((c) => (
              <div key={c} className="p-2.5 rounded-md bg-slate-50 border border-slate-200 text-center font-mono text-sm tracking-widest">
                {c}
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => { navigator.clipboard.writeText(backupCodes.join("\n")); toast.success("Códigos copiados"); }}
              className="px-4 py-2 border border-slate-300 rounded-md text-sm">Copiar todos</button>
            <button data-testid="backup-done" onClick={() => setBackupCodes([])}
              className="px-4 py-2 bg-[#0B2B62] text-white rounded-md text-sm font-semibold">Já guardei os códigos</button>
          </div>
        </section>
      ) : status ? (
        <section className="mt-6 bg-white border border-slate-200 rounded-xl p-6" data-testid="tfa-status-enabled">
          <div className="flex items-center gap-2 text-green-700 font-semibold">
            <ShieldCheck className="w-5 h-5" /> 2FA está ATIVO
          </div>
          {remaining != null && (
            <div className="mt-2 text-xs text-slate-500">Códigos de backup restantes: <b className="text-slate-700">{remaining}/10</b></div>
          )}
          <p className="text-sm text-slate-600 mt-2">Para desativar, informe um código válido do app autenticador.</p>
          <div className="mt-4 flex gap-2">
            <input data-testid="tfa-disable-code" value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="000000" inputMode="numeric" maxLength={6}
              className="w-32 px-3 py-2 border border-slate-300 rounded-md font-mono tracking-widest text-center outline-none focus:ring-2 focus:ring-[#0B2B62]" />
            <button data-testid="tfa-disable" onClick={disable} disabled={busy}
              className="px-4 py-2 border border-red-300 text-red-700 rounded-md font-semibold hover:bg-red-50 transition-colors disabled:opacity-60">
              Desativar 2FA
            </button>
          </div>
        </section>
      ) : !setup ? (
        <section className="mt-6 bg-white border border-slate-200 rounded-xl p-6">
          <div className="flex items-center gap-2 text-slate-500 font-medium">
            <KeyRound className="w-5 h-5" /> 2FA está desativado
          </div>
          <button data-testid="tfa-begin" onClick={beginSetup} disabled={busy}
            className="mt-4 px-5 py-2.5 bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-60">
            {busy ? "Gerando..." : "Ativar 2FA"}
          </button>
        </section>
      ) : (
        <section className="mt-6 bg-white border border-slate-200 rounded-xl p-6 space-y-5" data-testid="tfa-setup">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">1. Escaneie o QR Code no seu app autenticador</div>
            <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="QR Code" className="w-52 h-52 border border-slate-200 rounded-md" />
            <div className="mt-2 text-xs text-slate-500">Ou digite manualmente: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{setup.secret}</code></div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">2. Informe o código de 6 dígitos gerado</div>
            <div className="flex gap-2">
              <input data-testid="tfa-enable-code" value={code} onChange={(e) => setCode(e.target.value)}
                placeholder="000000" inputMode="numeric" maxLength={6}
                className="w-40 px-3 py-2.5 border border-slate-300 rounded-md font-mono tracking-widest text-center outline-none focus:ring-2 focus:ring-[#0B2B62]" />
              <button data-testid="tfa-enable" onClick={enable} disabled={busy || code.length !== 6}
                className="px-5 py-2.5 bg-green-600 text-white rounded-md font-semibold hover:bg-green-700 disabled:opacity-40">
                Confirmar e ativar
              </button>
              <button onClick={() => { setSetup(null); setCode(""); }} className="px-3 py-2.5 text-slate-500 hover:text-slate-800">Cancelar</button>
            </div>
            <p className="mt-2 text-xs text-slate-500">Ao ativar, você receberá 10 códigos de backup de uso único. Guarde em local seguro.</p>
          </div>
        </section>
      )}

      {user?.role === "consultor" && push.supported && (
        <section className="mt-8 bg-white border border-slate-200 rounded-xl p-6" data-testid="push-section">
          <div className="flex items-center gap-2 text-slate-700 font-semibold">
            <Bell className="w-5 h-5" /> Notificações de plantão
          </div>
          <p className="text-sm text-slate-600 mt-2">Receba alertas imediatos no navegador quando um caso <b>emergencial</b> entrar na fila. Ideal para quem está de plantão.</p>
          <div className="mt-3 text-xs text-slate-500">
            Suporte do navegador: <b>{push.supported ? "sim" : "não"}</b> · Permissão: <b>{push.permission}</b> · Inscrito: <b>{push.subscribed ? "sim" : "não"}</b>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            {!push.subscribed ? (
              <button data-testid="push-enable" onClick={async () => {
                setBusy(true);
                const r = await ensurePushSubscribed();
                setBusy(false);
                if (r.ok) { toast.success("Notificações ativadas"); loadStatus(); }
                else toast.error(r.reason === "denied" ? "Permissão negada no navegador" : "Não suportado neste navegador");
              }} disabled={busy}
                className="px-4 py-2 bg-[#0B2B62] text-white rounded-md font-semibold hover:bg-slate-800 disabled:opacity-60 inline-flex items-center gap-2">
                <Bell className="w-4 h-4" /> Ativar notificações
              </button>
            ) : (
              <>
                <button data-testid="push-test" onClick={async () => { await api.post("/push/test"); toast.success("Notificação de teste enviada"); }}
                  className="px-4 py-2 border border-slate-300 rounded-md font-semibold text-sm">Enviar teste</button>
                <button data-testid="push-disable" onClick={async () => { await unsubscribePush(); toast.success("Notificações desativadas"); loadStatus(); }}
                  className="px-4 py-2 border border-red-300 text-red-700 rounded-md font-semibold text-sm inline-flex items-center gap-2">
                  <BellOff className="w-4 h-4" /> Desativar
                </button>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default TwoFactor;
